import { memo, useCallback, useLayoutEffect, useMemo } from 'react';

import { observeElementRect, useVirtualizer } from '@tanstack/react-virtual';

import {
  CELL_GROUP_HEIGHT_PX,
  SCROLL_END_DEBOUNCE_MS,
  VIRTUALIZER_OVERSCAN_ROWS,
} from '../../constants';
import { RowItem } from '../../types';
import { TimelineRows } from './timeline-rows';

interface VirtualizedRowsProps {
  parentRef: React.RefObject<HTMLDivElement | null>;
  allRows: RowItem[];
  visibleRows: RowItem[];
  rowHeights: Map<string, number>;
}

/**
 * Custom observeElementOffset that throttles scroll offset reports to one per
 * animation frame AND skips horizontal-only scroll events.
 *
 * The default fires on every scroll event, each triggering a React re-render
 * via the virtualizer's onChange. This version:
 * 1. Coalesces to max one report per frame (RAF throttle)
 * 2. Skips the callback entirely when only the horizontal scroll position
 *    changed — the vertical virtualizer doesn't need to recalculate for
 *    horizontal scrolling, so this avoids a useless React reconciliation
 *    on every horizontal scroll frame.
 */
function throttledObserveElementOffset<T extends Element>(
  instance: { scrollElement: T | null; options: { horizontal?: boolean; isRtl?: boolean } },
  cb: (offset: number, isScrolling: boolean) => void
) {
  const element = instance.scrollElement;
  if (!element) return;

  let rafId = 0;
  let lastReportedOffset = 0;
  let idleTimer: ReturnType<typeof setTimeout> | null = null;

  const readOffset = () => {
    return instance.options.horizontal
      ? element.scrollLeft * (instance.options.isRtl ? -1 : 1)
      : element.scrollTop;
  };

  const handleScroll = () => {
    const currentOffset = readOffset();

    // Skip if offset hasn't changed (horizontal-only scroll for a vertical virtualizer)
    if (currentOffset === lastReportedOffset) {
      return;
    }

    if (!rafId) {
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        const offset = readOffset();
        // Double-check offset actually changed (may have bounced back during the frame)
        if (offset !== lastReportedOffset) {
          lastReportedOffset = offset;
          cb(offset, true);
        }
      });
    }

    // Debounce scrolling-end
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      const offset = readOffset();
      lastReportedOffset = offset;
      cb(offset, false);
      idleTimer = null;
    }, SCROLL_END_DEBOUNCE_MS);
  };

  // Initial report
  lastReportedOffset = readOffset();
  cb(lastReportedOffset, false);

  element.addEventListener('scroll', handleScroll, { passive: true });

  return () => {
    element.removeEventListener('scroll', handleScroll);
    if (rafId) cancelAnimationFrame(rafId);
    if (idleTimer) clearTimeout(idleTimer);
  };
}

/**
 * Owns the virtualizer so that scroll-driven re-renders are isolated here
 * and do not cascade through the heavy Planner parent component.
 *
 * Uses a custom RAF-throttled scroll observer so the virtualizer triggers
 * at most one React re-render per animation frame instead of one per scroll event.
 */
export const VirtualizedRows = memo(
  ({ parentRef, allRows, visibleRows, rowHeights }: VirtualizedRowsProps) => {
    const estimatedSizes = useMemo(
      () =>
        visibleRows.map(row => {
          const key = row.parentGroupId ? `${row.parentGroupId}-${row.id}` : row.id;
          return rowHeights.get(key) ?? CELL_GROUP_HEIGHT_PX;
        }),
      [visibleRows, rowHeights]
    );

    const observeOffset = useCallback(
      (
        instance: Parameters<typeof throttledObserveElementOffset>[0],
        cb: Parameters<typeof throttledObserveElementOffset>[1]
      ) => throttledObserveElementOffset(instance, cb),
      []
    );

    const virtualizer = useVirtualizer({
      count: visibleRows.length,
      getItemKey: index => {
        const row = visibleRows[index];
        if (!row) return index;
        return row.parentGroupId ? `${row.parentGroupId}-${row.id}` : row.id;
      },
      getScrollElement: () => parentRef.current,
      estimateSize: index => estimatedSizes[index],
      overscan: VIRTUALIZER_OVERSCAN_ROWS,
      observeElementOffset: observeOffset,
      observeElementRect,
    });

    // Remeasure virtualizer when data changes or collapse state changes.
    // useLayoutEffect ensures cached sizes are reset BEFORE paint so
    // getVirtualItems() returns correct translateY positions immediately.
    useLayoutEffect(() => {
      virtualizer.measure();
    }, [virtualizer, visibleRows, rowHeights]);

    return (
      <div className="relative w-full" style={{ height: `${virtualizer.getTotalSize()}px` }}>
        <TimelineRows
          allRows={allRows}
          rows={visibleRows}
          virtualItems={virtualizer.getVirtualItems()}
        />
      </div>
    );
  }
);
